import React from 'react';
import { PropTypes } from 'prop-types';
import ShipmentCard from 'components/orders/ShipmentCard';
import RecurringOrderCard from 'components/orders/RecurringOrderCard';
import PaginationButton from 'components/orders/PaginationButton';
import { constants as recurringOrderConstants } from 'utils/recurringOrder';
import SectionList from 'components/SectionList';
import SectionHeaders from 'components/orders/SectionHeaders';
import LoadSpinner from 'components/LoadSpinner';
import EmptyListPage from 'components/EmptyListPage';
import { isEmpty } from 'utils/types';
import Filters from 'components/orders/Filters';
import { useTranslation } from 'react-i18next';
import cx from 'classnames';
import { useSelector } from 'react-redux';
import { selectors as ordersSelectors } from 'state/orders';
import { IS_TENANT } from 'tenant/utils';

const OrdersList = ({ className, orders, type, isFetching }) => {
  const { t } = useTranslation('Orders');
  const data = orders
    ? Object.keys(orders).map((sectionHeader) => ({
        sectionHeader,
        data: orders[sectionHeader],
      }))
    : [];

  const filterFirstPickupTime = useSelector(
    ordersSelectors.getFilterFirstPickupTime
  );
  const filterLastDropoffTime = useSelector(
    ordersSelectors.getFilterLastDropoffTime
  );
  const filterUserId = useSelector(ordersSelectors.getFilterUserId);
  const hasActiveFilters =
    !!filterFirstPickupTime || !!filterLastDropoffTime || !!filterUserId;
  const isFilteredPreviousPage = type === 'previous' && hasActiveFilters;
  const isSearchPage = type === 'search';
  const showFilteredPage = isFilteredPreviousPage || isSearchPage;
  let emptyListPageType = showFilteredPage ? 'filtered' : type;

  const renderSectionHeader = ({ section: { sectionHeader } }) =>
    type === recurringOrderConstants.RECURRING
      ? SectionHeaders.getRecurringHeaderByStatus({
          status: sectionHeader,
          t,
        })
      : SectionHeaders.getDateHeaderByType({ value: sectionHeader, type, t });

  const renderItem = ({ item, isFirst }) =>
    type === recurringOrderConstants.RECURRING ? (
      <RecurringOrderCard orderId={item} type={type} isFirst={isFirst} />
    ) : (
      <ShipmentCard orderId={item} type={type} isTenant={IS_TENANT} />
    );

  const renderHeader = () => <Filters type={type} />;
  const renderBottom = () => (
    <PaginationButton type={type} label={t('fetch_more')} />
  );
  const keyExtractorSection = ({ section }) => section && section.date;
  const keyExtractorItem = ({ item }) => item;
  const renderLoading = () =>
    (isEmpty(orders) ||
      Object.values(orders).every((section) => isEmpty(section))) &&
    isFetching && <LoadSpinner size="12" />;
  const renderEmpty = () =>
    (isEmpty(orders) ||
      Object.values(orders).every((section) => isEmpty(section))) &&
    !isFetching && <EmptyListPage page={emptyListPageType} />;

  return (
    <SectionList
      renderLoading={renderLoading}
      renderEmpty={renderEmpty}
      sections={data}
      customClassName={cx(className, {
        'search-focus': type === 'search',
      })}
      renderItem={renderItem}
      renderSectionHeader={renderSectionHeader}
      renderHeader={renderHeader}
      renderBottom={renderBottom}
      keyExtractorSection={keyExtractorSection}
      keyExtractorItem={keyExtractorItem}
    />
  );
};

OrdersList.propTypes = {
  className: PropTypes.string,
  orders: PropTypes.object,
  type: PropTypes.string.isRequired,
  isFetching: PropTypes.bool,
};

OrdersList.defaultProps = {
  isFetching: false,
};

export default OrdersList;
